import { useCallback, useEffect, useRef } from 'react'
import {
  NetworkStatus,
  makeVar,
  useApolloClient,
  useMutation,
  useQuery,
  useReactiveVar,
  useSubscription
} from '@apollo/client'
import { DateTime, Duration, Interval } from 'luxon'
import { useIdleTimer } from 'react-idle-timer'

import {
  courseProgressionQuery,
  matchesQuery,
  matchesStatusQuery,
  newPendingMatchSubscription,
  pageSize,
  progressionPageSize,
  triggerAutomatchMutation,
  userQuery
} from 'src/graphql'
import type {
  CourseProgressionQuery,
  CourseProgressionQueryVariables,
  MatchFieldsFragment as Match,
  MatchProgressionFieldsFragment,
  MatchesQuery,
  MatchesQueryVariables,
  MatchesStatusQuery,
  MatchesStatusQueryVariables,
  NewPendingMatchSubscription,
  NewPendingMatchSubscriptionVariables,
  PendingMatchFieldsFragment as PendingMatch,
  TriggerAutomatchMutation,
  TriggerAutomatchMutationVariables
} from 'src/graphql/types'
import { MatchSource } from 'src/graphql/types'
import { sortByDate } from 'src/lib/utils/date'
import { pageToEdges } from 'src/lib/utils/transforms'
import { AnalyticsEvent, useEventAnalytics } from 'src/hooks/use-analytics'

const automatchDuration = Duration.fromObject({ hours: 2 })
const pollInterval = Duration.fromObject({ seconds: 30 }).as('milliseconds')
const togglePollingInterval = Duration.fromObject({ seconds: 5 }).as(
  'milliseconds'
)
const idleTimeout = Duration.fromObject({ minutes: 30 }).as('milliseconds')

// Which source tab the matches list shows. Module-level so every useMatches()
// consumer (the list, the reload button, the new-match check) observes the same
// matches({source}) cache entry without prop drilling.
export const activeMatchSource = makeVar<MatchSource>(MatchSource.Steam)

// How many matches each tab currently shows. Shared across every useMatches()
// consumer so all observers watch the same matches({first:N, source}) cache
// entry, and grows by a page on "Show more". Per-source so growing one tab's
// window doesn't inflate another tab's first fetch. Paginating by a growing
// `first` (rather than a cursor) means the frequent background refetches — the
// 30s pending-match poll, the pending-match subscription, the new-match check —
// re-pull the whole visible window instead of resetting it to the first page
// and dropping the extra pages.
const visibleMatchCounts = makeVar<Partial<Record<MatchSource, number>>>({})

export function useMatches() {
  const source = useReactiveVar(activeMatchSource)
  const first = useReactiveVar(visibleMatchCounts)[source] ?? pageSize

  const { data, previousData, error, loading, networkStatus, refetch } =
    useQuery<MatchesQuery, MatchesQueryVariables>(matchesQuery, {
      variables: { pageSize: first, source },
      // Each tab is its own cache entry ({pageSize, source}); under cache-first a
      // previously-viewed tab served a stale page until a full reload.
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true
    })

  // Keep the current window on screen while a larger one loads so growing the
  // list never flashes empty — but only within the same tab: on a tab switch
  // previousData is the old tab's list, so fall through to the spinner until
  // data for the newly selected source has actually loaded.
  const loadedSourceRef = useRef(source)
  useEffect(() => {
    if (data) loadedSourceRef.current = source
  }, [data, source])
  const visibleData =
    data ?? (loadedSourceRef.current === source ? previousData : undefined)

  // cache-and-network also reports loading while revalidating data we already
  // have, which would swap a tab's list or empty state for a spinner on every
  // visit. Only report loading when there is genuinely nothing to render yet.
  const isLoading = loading && !visibleData
  // Growing the window ("Show more") and switching tabs both change variables;
  // a background revalidation must not spin the button.
  const isFetchingMore = networkStatus === NetworkStatus.setVariables

  const hasMoreMatches = visibleData?.matches.pageInfo.hasNextPage ?? false

  const matches =
    (pageToEdges(visibleData?.matches) as (Match | PendingMatch)[]) ?? []

  const fetchMore = () => {
    const counts = visibleMatchCounts()
    visibleMatchCounts({
      ...counts,
      [source]: (counts[source] ?? pageSize) + pageSize
    })
  }

  return {
    refetch,
    loading: isLoading,
    fetchingMore: isFetchingMore,
    error,
    matches: sortByDate(['date', 'createdAt'], matches),
    hasMoreMatches,
    fetchMore
  }
}

// Trigger a refresh of the list without becoming a fetching observer of it.
// Consumers that only want to refresh (the reload button, the new-match check)
// used to call useMatches() for its refetch alone; under cache-and-network that
// makes each of them fire its own fetch of the whole window on every mount.
export function useRefetchMatches() {
  const client = useApolloClient()
  return useCallback(() => {
    client.refetchQueries({ include: [matchesQuery] })
  }, [client])
}
export function useTriggerAutomatch() {
  const automatchTriggeredEvent = useEventAnalytics(
    AnalyticsEvent.automatchTriggered
  )
  const automatchNotTriggeredEvent = useEventAnalytics(
    AnalyticsEvent.automatchNotTriggered
  )

  const { data, refetch, startPolling, stopPolling } = useQuery<
    MatchesStatusQuery,
    MatchesStatusQueryVariables
  >(matchesStatusQuery, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network'
  })

  const linkId = data?.user?.steamId

  const [triggerAutomatch] = useMutation<
    TriggerAutomatchMutation,
    TriggerAutomatchMutationVariables
  >(triggerAutomatchMutation, {
    refetchQueries: [{ query: userQuery }, { query: matchesStatusQuery }]
  })

  const trigger = useCallback(
    async (reason: string) => {
      if (!linkId) return
      try {
        await triggerAutomatch({ variables: { linkId, reason } })
        automatchTriggeredEvent({ automatch_trigger_reason: reason })
      } catch (err: any) {
        automatchNotTriggeredEvent({
          error: err?.message,
          automatch_trigger_reason: reason
        })
      }
    },
    [
      automatchTriggeredEvent,
      automatchNotTriggeredEvent,
      triggerAutomatch,
      linkId
    ]
  )

  return {
    linkId,
    data,
    refetch,
    startPolling,
    stopPolling,
    trigger
  }
}

export function useMatchesStatus() {
  const automatchSkippedTriggerEvent = useEventAnalytics(
    AnalyticsEvent.automatchSkippedTrigger
  )

  const { linkId, data, refetch, startPolling, stopPolling, trigger } =
    useTriggerAutomatch()

  const automatch = data?.user?.automatch
  const matchEdges = data?.latestMatches?.edges
  const latestMatchDate = matchEdges?.find((m) => !!m)?.node?.createdAt

  useSubscription<
    NewPendingMatchSubscription,
    NewPendingMatchSubscriptionVariables
  >(newPendingMatchSubscription, {
    onSubscriptionData: () => {
      refetch()
    }
  })

  useEffect(() => {
    const togglePolling = () => {
      const checkedAt = data?.user?.automatch?.checkedAt

      if (checkedAt) {
        const now = DateTime.local()

        const duration = Interval.fromDateTimes(
          DateTime.fromISO(checkedAt),
          now
        ).toDuration()

        if (duration.isValid) {
          duration > automatchDuration
            ? startPolling(pollInterval)
            : stopPolling()
        }
      }
    }

    const interval = globalThis.setInterval(
      togglePolling,
      togglePollingInterval
    )

    return () => {
      stopPolling()
      globalThis.clearInterval(interval)
    }
  })

  const skipTrigger = useCallback(
    (reason: string) => {
      automatchSkippedTriggerEvent({ automatch_skip_trigger_reason: reason })
    },
    [automatchSkippedTriggerEvent]
  )

  const handleActivity = () => {
    if (!linkId || !automatch) return skipTrigger('unlinked')
    if (!automatch.triggeredAt) return trigger('never_triggered')

    const triggeredAt = DateTime.fromISO(automatch.triggeredAt)
    if (triggeredAt.diffNow('minutes').minutes > -5)
      return skipTrigger('rate_limit')

    if (!latestMatchDate) return trigger('no_matches')
    if (!automatch.checkedAt) return trigger('never_checked')

    const matchDate = DateTime.fromISO(latestMatchDate)
    if (Math.abs(matchDate.diffNow('minutes').minutes) > 30) {
      return trigger('old_match')
    } else {
      return skipTrigger('recent_match')
    }
  }

  useIdleTimer({
    timeout: idleTimeout,
    crossTab: true,
    onActive: handleActivity,
    onAction: handleActivity,
    debounce: 5000
  })

  return { data }
}

export function useCourseProgression() {
  const { data, loading, refetch } = useQuery<
    CourseProgressionQuery,
    CourseProgressionQueryVariables
  >(courseProgressionQuery, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
    variables: { pageSize: progressionPageSize }
  })
  const edges = data?.progression?.edges
  const matches =
    edges &&
    (edges
      .filter((edge: any) => !!edge)
      .map((edge: any) => edge?.node)
      .filter((node: any) => !!node) as MatchProgressionFieldsFragment[])
  return { matches, loading, refetch }
}
